import { useEffect, useState } from "react";
import { Button, Stack } from "react-bootstrap";
import { useMediaQuery } from 'react-responsive';
import { Link, useLocation } from "react-router-dom";

import useUserStore from "../stores/User";

import supabase from "../utils/Supabase";
import { userHasBenefit } from "../utils/Benefits";
import ThemeToggle from "./ThemeToggle";
import { LoadingText } from "./LoadingText";

const currentSession = async () => {
  const { data, error } = await supabase.auth.getSession();
  if (error) {
    console.error("Error fetching session:", error.message);
    return null;
  }
  return data.session;
}

const signOut = async () => {
  const { error } = await supabase.auth.signOut();

  if (error) {
    return;
  }
};

export function SiteNav() {
  const location = useLocation();
  const userData = useUserStore((state) => state.data);
  const isLoading = useUserStore((state) => state.loading);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      const session = await currentSession();
      if (session && session.user) {
        setHasSession(true);
      }
    };

    fetchSession();
  }, []);

  if (hasSession && isLoading) {
    return (
      <nav className="my-auto">
        <Button className="btn btn-secondary navbutton disabled">
          <LoadingText text="Loading" className="mb-0" />
        </Button>
      </nav>
    );
  } else {
    return (
      <nav className="my-auto">
        <Link
          className={"btn btn-primary navbutton" + (location.pathname === '/about' ? ' btn-glow' : '')}
          to="/about"
        >
          About
        </Link>

        {/* Staged: staff-only until the first script is flipped to public. Drop
            this guard to surface Featured in the nav for everyone. */}
        {(userData?.isApprover || userData?.isFeatureAdmin) && (
          <Link
            className={"btn btn-primary navbutton" + (location.pathname.includes('/featured') ? ' btn-glow' : '')}
            to="/featured"
          >
            Featured
          </Link>
        )}

        <Link
          className={"btn btn-primary navbutton" + (location.pathname.includes('/database') ? ' btn-glow' : '')}
          to="/database"
        >
          Database
        </Link>

        {/* Show Platinum nav when their feature map includes platinum access */}
        {(userHasBenefit(userData, "platinumAccess") || userData?.isApprover) && (
          <Link
            className={"btn navbutton gray-button" + (location.pathname === '/platinumDatabase' ? ' btn-glow' : '')}
            to="/platinumDatabase"
          >
            Platinum
          </Link>
        )}

        <Link
          className={"btn btn-primary navbutton" + (location.pathname === '/requests' ? ' btn-glow' : '')}
          to="/requests"
        >
          Requests
        </Link>

        {(userData?.isIndexer || userData?.isApprover) && (
          <Link
            className={"btn btn-primary navbutton" + (location.pathname === '/indexerDashboard' ? ' btn-glow' : '')}
            to="/indexerDashboard"
          >
            Indexing
          </Link>
        )}

        {(userData?.isCurator || userData?.isApprover) && (
          <Link className={"btn btn-primary navbutton" + (location.pathname === '/curators' ? ' btn-glow' : '')} to="/curators">
            Curators
          </Link>
        )}

        {/* Roulette nav is gated by the roulette feature (admins always see it) */}
        {(userData?.isApprover || userHasBenefit(userData, "roulette")) && (
          <Link
            className={"btn btn-primary navbutton" + (location.pathname === '/roulette' ? ' btn-glow' : '')}
            to="/roulette"
          >
            <img src="/roulette-wheel.png" alt="Roulette" style={{ width: 18, height: 18, objectFit: 'contain', marginRight: 6, verticalAlign: 'middle' }} />Roulette
          </Link>
        )}

        {/* Feature admins land on a dashboard that only shows the Featured tab. */}
        {(userData?.isApprover || userData?.isFeatureAdmin) && (
          <Link
            className={"btn btn-primary navbutton" + (location.pathname === '/admin' ? ' btn-glow' : '')}
            to="/admin"
          >
            Admin
          </Link>
        )}

        {userData && (
          <span className="ms-2">
            <ThemeToggle />
          </span>
        )}
      </nav>
    );
  }
};

export function UserNav() {
  const location = useLocation();
  const userData = useUserStore((state) => state.data);
  const isLoading = useUserStore((state) => state.loading);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      const session = await currentSession();
      if (session && session.user) {
        setHasSession(true);
      }
    };

    fetchSession();
  }, []);

  if (hasSession && isLoading) {
    return null;
  } else {
    return (
      <nav className="my-auto">
        {userData ? (
          <Stack direction="horizontal" gap={2}>
            <Link
              className="btn btn-primary navbutton"
              to="/account"
              title="Access your account"
            >
              Account
            </Link>
            <Button
              variant="primary"
              className="navbutton"
              onClick={signOut}
              title="Sign out"
            >
              Sign out
            </Button>
          </Stack>
        ) : (
            <Link className="btn btn-primary navbutton" to={`/DatabaseLogin${location.pathname !== '/' ? `?redirectTo=${location.pathname}` : ''}`}>
            Log in
          </Link>
        )}

        {!userData && (
          <Link className="btn btn-primary navbutton" to="/register">
            Register
          </Link>
        )}
      </nav>
    );
  }
};

interface CombinedNavProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const CombinedNav: React.FC<CombinedNavProps> = ({ setOpen }) => {
  const location = useLocation();
  const userData = useUserStore((state) => state.data);
  const isSmall = useMediaQuery({ query: '(max-width: 576px)' });
  const isLoading = useUserStore((state) => state.loading);
  const [hasSession, setHasSession] = useState(false);

  useEffect(() => {
    const fetchSession = async () => {
      const session = await currentSession();
      if (session && session.user) {
        setHasSession(true);
      }
    };

    fetchSession();
  }, []);

  if (hasSession && isLoading) {
    return (
      <nav className="my-auto">
        <Button className="btn btn-secondary navbutton disabled">
          <LoadingText text="Loading" className="mb-0" />
        </Button>
      </nav>
    );
  } else {
    return (
      <nav>
        <Stack direction={"horizontal"} className="d-flex flex-wrap">
          <Link
            onClick={() => setOpen(false)}
            className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname === '/about' ? ' btn-glow' : '')}
            style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
            to="/about"
          >
            About
          </Link>

          {/* Staged: see the note in SiteNav above. */}
          {(userData?.isApprover || userData?.isFeatureAdmin) && (
            <Link
              onClick={() => setOpen(false)}
              className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname.includes('/featured') ? ' btn-glow' : '')}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to="/featured"
            >
              Featured
            </Link>
          )}

          <Link
            onClick={() => setOpen(false)}
            className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname.includes('/database') ? ' btn-glow' : '')}
            style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
            to="/database"
          >
            Database
          </Link>

          {(userHasBenefit(userData, "platinumAccess") || userData?.isApprover) && (
            <Link
              onClick={() => setOpen(false)}
              className={`btn gray-button navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname === '/platinumDatabase' ? ' btn-glow' : '')}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to="/platinumDatabase"
            >
              Platinum
            </Link>
          )}

          <Link
            onClick={() => setOpen(false)}
            className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname === '/requests' ? ' btn-glow' : '')}
            style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
            to="/requests"
          >
            Requests
          </Link>

          {(userData?.isIndexer || userData?.isApprover) && (
            <Link
              onClick={() => setOpen(false)}
              className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname === '/indexerDashboard' ? ' btn-glow' : '')}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to="/indexerDashboard"
            >
              Indexing
            </Link>
          )}

          {(userData?.isCurator || userData?.isApprover) && (
            <Link
              onClick={() => setOpen(false)}
              className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname === '/curators' ? ' btn-glow' : '')}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to="/curators"
            >
              Curators
            </Link>
          )}

          {(userData?.isApprover || userHasBenefit(userData, "roulette")) && (
            <Link
              onClick={() => setOpen(false)}
              className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname === '/roulette' ? ' btn-glow' : '')}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to="/roulette"
            >
              <img src="/roulette-wheel.png" alt="Roulette" style={{ width: 18, height: 18, objectFit: 'contain', marginRight: 6, verticalAlign: 'middle' }} />Roulette
            </Link>
          )}

          {(userData?.isApprover || userData?.isFeatureAdmin) && (
            <Link
              onClick={() => setOpen(false)}
              className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}` + (location.pathname === '/admin' ? ' btn-glow' : '')}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to="/admin"
            >
              Admin
            </Link>
          )}

          {userData ? (
            <>
              <Link
                onClick={() => setOpen(false)}
                className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}`}
                style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
                to="/account"
              >
                Account
              </Link>
              <Button
                variant="primary"
                className={`navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}`}
                style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
                onClick={signOut}
                title="Sign out"
              >
                Sign out
              </Button>
            </>
          ) : (
            <Link
              onClick={() => setOpen(false)}
              className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}`}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to={`/DatabaseLogin${location.pathname !== '/' ? `?redirectTo=${location.pathname}` : ''}`}
            >
              Log in
            </Link>
          )}

          {!userData && (
            <Link
              onClick={() => setOpen(false)}
              className={`btn btn-primary navbutton my-1 ${!isSmall ? "flex-grow-1" : ""}`}
              style={isSmall ? { flexBasis: 'calc(50% - 0.5rem)' } : {}}
              to="/register"
            >
              Register
            </Link>
          )}
        </Stack>
      </nav>
    );
  }
};